/**
 * @file     publicTools.ts
 * @purpose  Single source of truth for the PUBLIC (no-account) tools surfaced on the
 *           /tools hub and their per-tool marketing pages. Structural only (routes,
 *           icon topic, preview kind, hub group); user-facing copy lives in the
 *           tools view copy.
 */
import { routes } from '@/core/constants';

export type PublicToolSlug =
  | 'mortgage-calculator'
  | 'stress-test'
  | 'pre-approval-check'
  | 'affordability'
  | 'rent-vs-buy'
  | 'rent-vs-mortgage'
  | 'cost-of-buying'
  | 'bc-property-taxes'
  | 'first-time-buyer-programs'
  | 'home-value'
  | 'cost-of-selling'
  | 'compare'
  | 'similar'
  | 'net-proceeds'
  | 'cost-of-waiting'
  | 'depreciation'
  | 'property-transfer-tax'
  | 'gst-new-home-rebate'
  | 'closing-costs'
  | 'home-flipping-tax'
  | 'speculation-vacancy-tax';
export type ToolPreviewKind = 'compare' | 'similar' | 'net-proceeds' | 'cost-of-waiting' | 'depreciation' | 'calculator' | 'guide';

/**
 * Tools that explain one cost of buying or owning in BC. They cross-link to
 * each other so none of them is an orphan, and the buyer cost pages link in.
 */
export type PublicToolGroup = 'buyer-costs';

/** Section of the /tools hub the tool is listed under. */
export type PublicToolHubGroup = 'buying' | 'selling' | 'comparing';

export interface PublicToolMeta {
  slug: PublicToolSlug;
  /** Interactive + marketing route (marketing copy is the empty state / above-the-fold). */
  route: string;
  /** resolveTopicIcon() key. */
  topic: string;
  previewKind: ToolPreviewKind;
  /** nav.items key for the short link label, where the tool is in a cross-link group. */
  labelKey?: string;
  group?: PublicToolGroup;
  hub: PublicToolHubGroup;
}

export const PUBLIC_TOOLS: PublicToolMeta[] = [
  { slug: 'mortgage-calculator', route: routes.mortgageCalculator, topic: 'mortgage', previewKind: 'calculator', hub: 'buying' },
  { slug: 'stress-test', route: routes.mortgageStressTest, topic: 'shield', previewKind: 'calculator', hub: 'buying' },
  { slug: 'pre-approval-check', route: routes.preApprovalCheck, topic: 'finance', previewKind: 'calculator', hub: 'buying' },
  { slug: 'affordability', route: routes.affordability, topic: 'finance', previewKind: 'calculator', hub: 'buying' },
  { slug: 'rent-vs-buy', route: routes.rentVsBuy, topic: 'compare', previewKind: 'calculator', hub: 'buying' },
  { slug: 'rent-vs-mortgage', route: routes.rentVsMortgage, topic: 'finance', previewKind: 'calculator', hub: 'buying' },
  { slug: 'cost-of-buying', route: routes.costOfBuying, topic: 'money', previewKind: 'guide', hub: 'buying' },
  {
    slug: 'property-transfer-tax',
    route: routes.propertyTransferTaxCalculator,
    topic: 'finance',
    previewKind: 'calculator',
    labelKey: 'nav.items.propertyTransferTaxCalculator',
    group: 'buyer-costs',
    hub: 'buying',
  },
  {
    slug: 'gst-new-home-rebate',
    route: routes.gstNewHomeRebate,
    topic: 'finance',
    previewKind: 'calculator',
    labelKey: 'nav.items.gstNewHomeRebate',
    group: 'buyer-costs',
    hub: 'buying',
  },
  {
    slug: 'closing-costs',
    route: routes.closingCosts,
    topic: 'finance',
    previewKind: 'calculator',
    labelKey: 'nav.items.closingCosts',
    group: 'buyer-costs',
    hub: 'buying',
  },
  { slug: 'bc-property-taxes', route: routes.bcPropertyTaxes, topic: 'document', previewKind: 'guide', hub: 'buying' },
  {
    slug: 'speculation-vacancy-tax',
    route: routes.speculationVacancyTax,
    topic: 'finance',
    previewKind: 'guide',
    labelKey: 'nav.items.speculationVacancyTax',
    hub: 'buying',
  },
  { slug: 'first-time-buyer-programs', route: routes.firstTimeBuyerPrograms, topic: 'document', previewKind: 'guide', hub: 'buying' },
  { slug: 'home-value', route: routes.homeValue, topic: 'search', previewKind: 'calculator', hub: 'selling' },
  { slug: 'cost-of-selling', route: '/cost-of-selling-a-home-bc', topic: 'money', previewKind: 'calculator', hub: 'selling' },
  {
    slug: 'net-proceeds',
    route: routes.homeSaleProceedsCalculator,
    topic: 'finance',
    previewKind: 'net-proceeds',
    hub: 'selling',
  },
  {
    slug: 'cost-of-waiting',
    route: routes.shouldISellNow,
    topic: 'finance',
    previewKind: 'cost-of-waiting',
    hub: 'selling',
  },
  {
    slug: 'home-flipping-tax',
    route: routes.bcHomeFlippingTax,
    topic: 'finance',
    previewKind: 'calculator',
    labelKey: 'nav.items.bcHomeFlippingTax',
    hub: 'selling',
  },
  { slug: 'similar', route: routes.smartMatch, topic: 'search', previewKind: 'similar', hub: 'comparing' },
  { slug: 'compare', route: routes.compare, topic: 'compare', previewKind: 'compare', hub: 'comparing' },
  {
    slug: 'depreciation',
    route: routes.depreciationReport,
    topic: 'search',
    previewKind: 'depreciation',
    hub: 'comparing',
  },
];

export const getPublicTool = (slug: PublicToolSlug): PublicToolMeta | undefined =>
  PUBLIC_TOOLS.find((t) => t.slug === slug);

/** Members of a cross-link group, minus the page asking (so it never links to itself). */
export const publicToolGroup = (group: PublicToolGroup, excludeRoute?: string): PublicToolMeta[] =>
  PUBLIC_TOOLS.filter((t) => t.group === group && t.route !== excludeRoute);

/** Hub sections in display order, each with its tools in catalogue order. */
export const PUBLIC_TOOL_HUB_GROUPS: PublicToolHubGroup[] = ['buying', 'selling', 'comparing'];

export const publicToolsInHubGroup = (hub: PublicToolHubGroup): PublicToolMeta[] =>
  PUBLIC_TOOLS.filter((t) => t.hub === hub);
